import React, { useRef } from 'react';
import NumberProperty from './NumberProperty';
import PropertyBase, { IBasePropertyProps } from './PropertyBase';
import AnalyticsManager from '../../classes/AnalyticsManager';

interface ISliderPropertyProps extends IBasePropertyProps
{
	/** the minimum value of the internal slider and input field */
	min?: number,
	/** the maximum value the internal slider and input field */
	max?: number,
	/** the increment value used by the slider and the internal input field when scrolling with the mouse or using the inline arrows. *defaults to 1* */
	step?: number,
	baseStyle?: React.CSSProperties,
	/** when set to true, the manual input field will not display */
	hideField?: boolean,	
	showMinMax?: boolean,
	onBlur?: (field: HTMLInputElement) => void,
	onFocus?: (field: HTMLInputElement) => void,
	decimals?: number,
	cellclass?: string,
}

/**
 * defines a slider input with an optional input field for manual value insertion  
 * *internally uses a NumberProperty*
 *
 * @export
 * @param {ISliderPropertyProps} props
 * @return {*} 
 */
export default function SliderProperty(props: ISliderPropertyProps)
{
	const min = props.min !== undefined ? props.min : 0;
	const max = props.max !== undefined ? props.max : 1;

	const tempValue = useRef(props.value);
	const sliderRef = useRef<HTMLInputElement>(null);

	function onChangeEnd()
	{
		document.removeEventListener('mouseup', onChangeEnd);

		if (sliderRef.current)
		{
			AnalyticsManager.managedEvent('propertyChanged', 'propertyId', sliderRef.current.dataset.name);
			sliderRef.current.blur();
		}

		if (props.onCommitCallback)
			props.onCommitCallback(tempValue.current);
	}

	const dataName = (props["data-name"] ? props["data-name"] + "-" : "") + "-slider-property";

	return (
		<PropertyBase {...props}
			data-name={dataName + "root"}
			style={{...props.style, ...props.baseStyle}}
			labelClass={props.labelClass}
		>
			{
				props?.hideField ? null :
					<NumberProperty
						cellclass={"narrowField"}
						data-name={dataName + "-root"}
						value={props.value}
						readOnly={props.readOnly}
						step={props.step ? props.step : 1}
						min={min}
						max={max}
						multivalues={props.multivalues}
						onChangeCallback={props.onChangeCallback}
						onCommitCallback={props.onCommitCallback}
						onBlur={props.onBlur}
						onFocus={props.onFocus}
						decimals={props.decimals}
						contextMenuOverride={props.contextMenuOverride}
					/>
			}
			{
				!props.showMinMax ? null :
					<div data-cellclass={"sliderMinMaxLabel min"}>
						{props.min}
					</div>
			}
			<input data-name={dataName + "-slider"}
				className={["sliderField", (props.multivalues ? "multivalue" : ""), props.className].join(' ')}
				type="range"
				ref={sliderRef}
				value={props.value}
				disabled={props.readOnly}
				min={min}
				max={max}
				step={props.step ? props.step : "any"}
				lang={'en'}
				onChange={(e) =>
				{
					tempValue.current = e.target.value;

					if (props.onChangeCallback)
						props.onChangeCallback(Number(tempValue.current));
				}}
				onMouseDown={() => document.addEventListener('mouseup', onChangeEnd)}
				onDoubleClick={() =>
				{
					if (props.defaultValue === undefined)
						return;

					if (props.onChangeCallback)
					{
						props.onChangeCallback(props.defaultValue);
					}

					if (props.onCommitCallback)
					{
						props.onCommitCallback(props.defaultValue);
					}
				}}
			/>
			{
				!props.showMinMax ? null :
					<div data-cellclass={"sliderMinMaxLabel max"}>
						{props.max}
					</div>
			}
		</PropertyBase>
	);
}